'use client';

import type {
  PropsWithChildren} from 'react';
import {
  createContext,
  Fragment,
  useContext,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import classNames from 'classnames';

import type { TransitionSpeed } from '@ui/uikit/styles/exports/effects';

import { Transition } from '../../Transition';
import styles from './Overlays.module.scss';

interface OverlaysContextValue {
  containers: Record<string, Element | null>;
}

const OverlaysContext = createContext<OverlaysContextValue>({
  containers: {},
});

interface OverlaysProviderProps extends PropsWithChildren {
  layers?: Array<Exclude<string, 'default'>>;
}

const OverlaysProvider = ({ layers = [], children }: OverlaysProviderProps) => {
  const id = useId();

  const [containers, setContainers] = useState<Record<string, Element | null>>({});

  const layersWithDefault = useMemo(() => {
    return [...layers, 'default'];
  }, [layers]);

  return (
    <Fragment>
      {<OverlaysContext.Provider value={{ containers }}>{children}</OverlaysContext.Provider>}

      <div className={styles['container']}>
        {layersWithDefault.map((layer) => {
          return (
            <div
              id={`overlays${id}-layer-${layer}`}
              className={classNames(styles['layer'])}
              ref={(element) => {
                if (element && containers[layer] !== element) {
                  setContainers((current) => ({ ...current, [layer]: element }));
                }
              }}
              key={layer}
            ></div>
          );
        })}
      </div>
    </Fragment>
  );
};

interface OverlayProps extends PropsWithChildren {
  in?: boolean;
  layer?: string;
  transitionSpeed?: TransitionSpeed;
}

const Overlay = ({ in: _in = true, layer = 'default', children }: OverlayProps) => {
  const { containers } = useContext(OverlaysContext);

  const container = useMemo(() => {
    return containers[layer] ?? containers['default'];
  }, [containers, layer]);

  const containerRef = useRef<HTMLDivElement>(null);

  return container ? createPortal((
    <Transition
      in={_in}
      speed="medium"
      nodeRef={containerRef}
    >
      <div
        className={classNames(styles['overlay'])}
        ref={containerRef}
      >
        {children}
      </div>
    </Transition>
  ), container) : false;
};

export { Overlay, OverlaysProvider };
